import {Component, property} from '@wonderlandengine/api';
import {LeaderboardEntry, leaderboards, user} from '@wonderlandengine/uber-sdk';
import {GameModeType} from '../enums/GameModeType.js';

export type LeaderboardData = {
    username: string;
    score: number;
};

export class LeaderboardManager extends Component {
    static TypeName = 'leaderboard-manager';

    @property.bool(true)
    debug: Boolean = true;

    private static _instance: LeaderboardManager;
    static get instance(): LeaderboardManager {
        return LeaderboardManager._instance;
    }

    leaderboardData: Map<GameModeType, LeaderboardData[]> = new Map(); // Cache the leaderboard data

    init() {
        if (LeaderboardManager._instance) {
            console.error('There can only be one instance of LeaderboardManager Component');
        }
        LeaderboardManager._instance = this;

        // Load leaderboard on initialization
        this.loadLeaderboard(GameModeType.Quickplay);
        this.loadLeaderboard(GameModeType.Easy);
    }

    /**
     * Load the leaderboard data from the server.
     */
    async loadLeaderboard(mode: GameModeType) {
        const response = await leaderboards.getScores(this._toBoardName(mode), 10);

        const data = response.map((entry: LeaderboardEntry) => ({
            username: entry.user,
            score: entry.score,
        }));

        this.leaderboardData.set(mode, data);
    }

    /**
     * Get the username of a specific rank.
     * @param rank - Rank in the leaderboard (1-based index).
     * @returns Username at the specified rank; null if not found.
     */
    getUserName(mode: GameModeType, rank: number): string {
        if (!this.leaderboardData.has(mode)) {
            return null;
        }
        return this.leaderboardData.get(mode)[rank - 1]?.username;
    }

    /**
     * Get the score of a specific rank.
     * @param rank - Rank in the leaderboard (1-based index).
     * @returns Score at the specified rank.
     */
    getScore(mode: GameModeType, rank: number): number {
        if (!this.leaderboardData.has(mode)) {
            return null;
        }
        return this.leaderboardData.get(mode)[rank - 1]?.score;
    }

    /**
     * Get the rank of the current user.
     * @param username - The username to find the rank for.
     * @returns The rank of the user, or -1 if not found.
     */
    getUserRank(mode: GameModeType): number {
        if (!this.leaderboardData.has(mode)) {
            return -1;
        }

        if (!user.isLoggedIn) {
            return -1;
        }

        const username = user.user.name;
        const rank = this.leaderboardData
            .get(mode)
            .findIndex((entry) => entry.username === username);
        return rank !== -1 ? rank + 1 : -1;
    }

    //TK: Function originally came from BackendInterface.cs. Implementation is different, but kept the name for reference.
    async saveScoreToPlatformLeaderboard(score: number, mode: GameModeType) {
        await leaderboards.postScore(this._toBoardName(mode), score);
        await this.loadLeaderboard(mode);
    }

    async getLeaderboard(mode: GameModeType): Promise<LeaderboardData[]> {
        if (!this.leaderboardData.has(mode)) {
            await this.loadLeaderboard(mode);
        }
        return this.leaderboardData.get(mode);
    }

    private _toBoardName(mode: GameModeType): string {
        return GameModeType[mode].toLocaleLowerCase();
    }
}
